import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import saveAs from "file-saver";
import { getFunctions, httpsCallable, HttpsCallableResult } from "firebase/functions";
import { t } from "i18next";
import JSZip from "jszip";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ModalPopup } from "../../../../../components/deprecated/ModalPopup/ModalPopup";
import { ControlledCheckbox } from "../../../../../components/ui-components/Checkbox/ControlledCheckbox";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { DeviationData } from "../../../../../to-be-removed/LocalData/Deviation/DeviationData";
import { convertDataURLtoBlob } from "@src/utils/blob.utils";

const firebaseFunctions = getFunctions(undefined, "europe-west1");

const getDeviationPdf = httpsCallable(firebaseFunctions, "getDeviationPdf");

export type ExportDeviationPDFsPopupProps = {
  id: string;
  deviations: DeviationData[];
  onFinished?: () => void;
};

type FormFieldValues = {
  files: boolean;
  checklist: boolean;
  costs: boolean;
  externalLinks: boolean;
  activities: boolean;
};

const defaultValues: FormFieldValues = {
  files: true,
  checklist: true,
  costs: true,
  externalLinks: true,
  activities: true,
};

export const ExportDeviationPDFsPopup = ({ id, deviations, onFinished }: ExportDeviationPDFsPopupProps) => {
  const [includeAllData, setIncludeAllData] = useState(true);

  const { handleSubmit, control, reset } = useForm<FormFieldValues>({
    defaultValues,
  });

  useEffect(() => {
    if (includeAllData) {
      reset(defaultValues);
    }
  }, [includeAllData, reset]);

  const exportPdfs = (values: FormFieldValues) => {
    const resultPDFs: Promise<HttpsCallableResult<any>>[] = deviations
      .filter((deviation) => deviation.parent) // Only include deviations with a parent
      .map((deviation) =>
        getDeviationPdf({
          propertyID: deviation.parent?.id,
          deviationID: deviation.id,
          contentSettings: values,
        })
      );

    const promises = Promise.all(resultPDFs)
      .then((result) => {
        if (result.length === 1) {
          saveAs(result[0].data, `${deviations[0].title}.pdf`);
          onFinished?.();
        } else if (result.length > 1) {
          const zip = new JSZip();
          result.forEach((pdf, index) => {
            const fileName = `${deviations[index].deviationNumber} ${deviations[index].title}.pdf`;
            const pdfBlob = convertDataURLtoBlob(pdf.data);
            zip.file(fileName, pdfBlob, { binary: true });
          });
          return zip.generateAsync({ type: "blob" }).then((content) => {
            saveAs(content, `${t("main.tasks")}.zip`);
            onFinished?.();
          });
        }
      })
      .catch((error) => {
        ToastUtilities.presentServerError(error);
        throw error;
      });

    ToastUtilities.presentPromise(`${t("main.generatingPdf")}...`, promises);
  };

  return (
    <ModalPopup
      id={id}
      title={t("main.exportPDF")}
      onActionClicked={handleSubmit(exportPdfs)}
      actionTitle={t("main.download")}
      children={
        <div
          style={{
            display: "flex",
            gap: "1em",
            flexDirection: "column",
          }}
        >
          <Checkbox onChange={setIncludeAllData} value={includeAllData} description={t("main.includeAllData")} />
          {!includeAllData && (
            <>
              <Checkbox disabled onChange={() => void 0} value={true} description={t("main.generalInformation")} />
              <ControlledCheckbox control={control} name="files" description={t("main.files")} />
              <ControlledCheckbox control={control} name="checklist" description={t("main.checklists")} />
              <ControlledCheckbox control={control} name="costs" description={t("main.costs")} />
              <ControlledCheckbox control={control} name="externalLinks" description={t("main.externalLinks")} />
              <ControlledCheckbox control={control} name="activities" description={t("main.activityLog")} />
            </>
          )}
        </div>
      }
    />
  );
};
